import { createClient, Client } from 'graphql-ws';
import { getJWTTokens } from '@shared/libs/local-storage';
import { getWsEndpoint } from '@shared/api/api-url';
import { ensureValidAccessToken } from '@shared/api/ws-auth';
import { mapSminexClubClientsByEventPayload } from './mapPayload';
import { buildSminexClubClientsByEventSubscription, SminexClubClientsByEventPayload } from './subscription';
import {
  SminexClubClientsWsListener,
  SminexClubClientsWsState,
  SminexClubClientsWsStatus,
  SminexClubWsClient,
} from './types';

const INITIAL_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 15000;
const MAX_RETRY_ATTEMPTS = 5;

export interface ClientTicketPatch {
  review?: string;
  comment?: string;
  sminexClubInvitation?: string;
  invitedByManager?: string;
  mobileComment?: string;
}

let clients: SminexClubWsClient[] = [];
let status: SminexClubClientsWsStatus = 'idle';
let activeEventId = 0;
let client: Client | null = null;
let unsubscribe: (() => void) | null = null;
let retryTimeout: ReturnType<typeof setTimeout> | null = null;
let backoffMs = INITIAL_BACKOFF_MS;
let retryAttemptCount = 0;
let generation = 0;
const pendingTicketPatches = new Map<number, ClientTicketPatch>();

const listeners = new Set<SminexClubClientsWsListener>();

const getState = (): SminexClubClientsWsState => ({
  clients,
  status,
  eventId: activeEventId,
});

const notify = () => {
  const state = getState();
  listeners.forEach((listener) => listener(state));
};

const setStatus = (next: SminexClubClientsWsStatus) => {
  if (status === next) return;
  status = next;
  notify();
};

const resetRetryState = () => {
  retryAttemptCount = 0;
  backoffMs = INITIAL_BACKOFF_MS;
};

const setClients = (next: SminexClubWsClient[]) => {
  clients = next;
  status = 'ready';
  resetRetryState();
  notify();
};

const applyPatchToTicket = (
  ticket: SminexClubWsClient['ticket'],
  patch: ClientTicketPatch
): SminexClubWsClient['ticket'] => ({
  ...ticket,
  ...(patch.review !== undefined ? { review: patch.review } : {}),
  ...(patch.comment !== undefined ? { comment: patch.comment } : {}),
  ...(patch.mobileComment !== undefined ? { mobileComment: patch.mobileComment } : {}),
  ...(patch.sminexClubInvitation !== undefined ? { sminexClubInvitation: patch.sminexClubInvitation } : {}),
  ...(patch.invitedByManager !== undefined
    ? { invitedByManager: patch.invitedByManager, organizerName: patch.invitedByManager }
    : {}),
});

const isPendingFieldConfirmed = (
  ticket: SminexClubWsClient['ticket'],
  field: keyof ClientTicketPatch,
  value: string
) => {
  if (field === 'invitedByManager') {
    return ticket.invitedByManager === value || ticket.organizerName === value;
  }

  if (field === 'comment') {
    return (ticket.comment ?? '') === value;
  }

  return ticket[field] === value;
};

const mergeClientsWithPendingPatches = (next: SminexClubWsClient[]): SminexClubWsClient[] => {
  if (pendingTicketPatches.size === 0) return next;

  return next.map((clientItem) => {
    const pending = pendingTicketPatches.get(clientItem.ticket.id);
    if (!pending) return clientItem;

    const remaining: ClientTicketPatch = {};

    (Object.keys(pending) as (keyof ClientTicketPatch)[]).forEach((field) => {
      const value = pending[field];
      if (value === undefined) return;

      if (!isPendingFieldConfirmed(clientItem.ticket, field, value)) {
        remaining[field] = value;
      }
    });

    if (Object.keys(remaining).length === 0) {
      pendingTicketPatches.delete(clientItem.ticket.id);
      return clientItem;
    }

    pendingTicketPatches.set(clientItem.ticket.id, remaining);

    return {
      ...clientItem,
      ticket: applyPatchToTicket(clientItem.ticket, remaining),
    };
  });
};

export const applyClients = (next: SminexClubWsClient[], eventId: number) => {
  activeEventId = eventId;
  setClients(mergeClientsWithPendingPatches(next));
};

export const markClientsConnecting = (eventId: number) => {
  activeEventId = eventId;
  setStatus('connecting');
};

export const markClientsError = (eventId: number) => {
  activeEventId = eventId;
  setStatus('error');
};

export const patchClientTicket = (ticketId: number, patch: ClientTicketPatch) => {
  const previous = pendingTicketPatches.get(ticketId) ?? {};
  pendingTicketPatches.set(ticketId, { ...previous, ...patch });

  let changed = false;

  clients = clients.map((clientItem) => {
    if (clientItem.ticket.id !== ticketId) return clientItem;

    changed = true;

    return {
      ...clientItem,
      ticket: applyPatchToTicket(clientItem.ticket, patch),
    };
  });

  if (changed) {
    notify();
  }
};

const clearRetryTimeout = () => {
  if (!retryTimeout) return;
  clearTimeout(retryTimeout);
  retryTimeout = null;
};

const disposeClient = () => {
  unsubscribe?.();
  unsubscribe = null;

  if (!client) return;

  const activeClient = client;
  client = null;
  void Promise.resolve(activeClient.dispose()).catch(() => {});
};

const isUnauthorizedError = (errors: readonly { message?: string }[] | undefined): boolean =>
  !!errors?.some((error) => error.message?.toLowerCase().includes('unauthorized'));

const isUnauthorizedTransportError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const { code, reason, message } = error as { code?: unknown; reason?: unknown; message?: unknown };
  let errorText = '';
  if (typeof reason === 'string') {
    errorText = reason;
  }
  if (!errorText && typeof message === 'string') {
    errorText = message;
  }

  return code === 4401 || code === 4403 || errorText.toLowerCase().includes('unauthorized');
};

const scheduleRetry = (currentGeneration: number, eventId: number) => {
  clearRetryTimeout();

  if (retryAttemptCount >= MAX_RETRY_ATTEMPTS) {
    setStatus('error');
    return;
  }

  retryAttemptCount += 1;

  retryTimeout = setTimeout(() => {
    retryTimeout = null;
    if (currentGeneration !== generation) return;
    backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
    void connect(currentGeneration, eventId);
  }, backoffMs);
};

const connect = async (currentGeneration: number, eventId: number) => {
  if (currentGeneration !== generation) return;
  if (!eventId) {
    setStatus('error');
    return;
  }

  clearRetryTimeout();
  disposeClient();
  setStatus('connecting');

  const accessToken = await ensureValidAccessToken();
  if (currentGeneration !== generation) return;

  if (!accessToken) {
    setStatus('error');
    return;
  }

  const wsUrl = getWsEndpoint();
  let receivedClients = false;

  try {
    client = createClient({
      url: wsUrl,
      lazy: false,
      retryAttempts: 0,
      connectionParams: () => {
        const { accessToken: token } = getJWTTokens();
        return {
          Authorization: `Bearer ${token}`,
        };
      },
    });

    unsubscribe = client.subscribe(
      { query: buildSminexClubClientsByEventSubscription(eventId) },
      {
        next: (result) => {
          if (currentGeneration !== generation) return;

          if (result.errors?.length) {
            disposeClient();
            if (isUnauthorizedError(result.errors)) {
              setStatus('error');
              return;
            }
            scheduleRetry(currentGeneration, eventId);
            return;
          }

          const mapped = mapSminexClubClientsByEventPayload(result.data as SminexClubClientsByEventPayload | undefined);
          if (!mapped) return;

          receivedClients = true;
          setClients(mapped);
          disposeClient();
        },
        error: (error) => {
          if (currentGeneration !== generation || receivedClients) return;
          disposeClient();
          if (isUnauthorizedTransportError(error)) {
            setStatus('error');
            return;
          }
          scheduleRetry(currentGeneration, eventId);
        },
        complete: () => {
          if (currentGeneration !== generation || receivedClients) return;
          disposeClient();
          scheduleRetry(currentGeneration, eventId);
        },
      }
    );
  } catch {
    disposeClient();
    scheduleRetry(currentGeneration, eventId);
  }
};

export const getClients = (): SminexClubWsClient[] => clients;

export const getStatus = (): SminexClubClientsWsStatus => status;

export const getEventId = (): number => activeEventId;

export const subscribe = (listener: SminexClubClientsWsListener): (() => void) => {
  listeners.add(listener);
  listener(getState());
  return () => {
    listeners.delete(listener);
  };
};

export const closeConnection = () => {
  clearRetryTimeout();
  disposeClient();
  if (status === 'connecting' || status === 'error') {
    setStatus(clients.length ? 'ready' : 'idle');
  }
};

export const clearSminexClubClients = () => {
  generation += 1;
  clearRetryTimeout();
  disposeClient();
  clients = [];
  status = 'idle';
  activeEventId = 0;
  pendingTicketPatches.clear();
  resetRetryState();
  notify();
};

export const ensureSminexClubClientsLoaded = (options: { eventId: number; force?: boolean }) => {
  const { eventId, force = false } = options;

  if (!eventId) return;

  if (activeEventId !== eventId) {
    generation += 1;
    disposeClient();
    clients = [];
    status = 'idle';
    activeEventId = eventId;
    pendingTicketPatches.clear();
    resetRetryState();
    notify();
  }

  if (status === 'ready' && !force) return;
  if (status === 'connecting' && !force) return;

  if (status === 'error' || force) {
    if (status === 'error' && !force) return;
    generation += 1;
    disposeClient();
    clients = [];
    resetRetryState();
    status = 'idle';
    notify();
  }

  if (status === 'ready') return;
  if (status === 'connecting') return;

  const currentGeneration = generation;
  void connect(currentGeneration, eventId);
};
